import React from 'react';
import { Autocomplete, CircularProgress, Popper, TextField } from '@mui/material';
import useGetAllVendors from '../../hooks/vendors/useGetAllVendors';
import { Vendor } from '../../../types/vendor-types';
import DialogGlynt, { FormControlStyled, FormLabelStyled } from './DialogGlynt';
import DialogTitleGlynt from './DialogTitleGlynt';
import DialogContentGlynt from './DialogContentGlynt';
import DialogActionsGlynt from './DialogActionsGlynt';

interface ISetVendorDialogProps {
  open: boolean;
  currentVendorId?: number | null;
  isUpdating: boolean;
  onClose: () => void;
  onSubmit: (vendorId: number | null) => void;
}

const SetVendorDialog: React.FC<ISetVendorDialogProps> = ({
  open,
  currentVendorId,
  isUpdating,
  onClose,
  onSubmit,
}) => {
  const [selectedVendorId, setSelectedVendorId] = React.useState<number | null>(
    currentVendorId ?? null
  );

  const { data: vendors, isLoading } = useGetAllVendors();

  // Reset selected vendor when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelectedVendorId(currentVendorId ?? null);
    }
  }, [open, currentVendorId]);

  const handleSubmit = () => {
    onSubmit(selectedVendorId);
  };

  return (
    <DialogGlynt
      open={open}
      closeModal={onClose}
      width="500px"
      cypressId="dialog-set-vendor"
    >
      <DialogTitleGlynt title="Set Vendor" closeModal={onClose} />
      <DialogContentGlynt>
        <FormControlStyled fullWidth>
          <FormLabelStyled>VENDOR</FormLabelStyled>
          <Autocomplete
            fullWidth
            options={vendors || []}
            value={(vendors || []).find((v) => v.id === selectedVendorId) || null}
            onChange={(event, newValue: Vendor | null) => {
              setSelectedVendorId(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.name}
            disabled={isLoading}
            PopperComponent={(props) => (
              <Popper data-cy="popper-select-model-popup" {...props} placement="bottom-start" />
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder="Search vendor"
                data-cy="select-vendor"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </FormControlStyled>
      </DialogContentGlynt>
      <DialogActionsGlynt
        handleSubmit={handleSubmit}
        closeModal={onClose}
        cancelCypressId="btn-set-vendor-cancel"
        submitCypressId="btn-set-vendor-submit"
        clearCypressId="btn-set-vendor-clear"
        submitButtonText="Submit"
        submitLoading={isUpdating}
        showCancelButton={true}
        showClearButton={false}
        submitDisabled={isUpdating}
      />
    </DialogGlynt>
  );
};

export default SetVendorDialog;
